import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuditUser } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

const PermissionsContext = createContext(null);

// All page/section keys in the system
export const ALL_PERMISSIONS = [
  { pageKey: 'game-planner', sectionKey: 'game-planner', label: 'Game Planner', pageLabel: 'Game Planner' },
  // Each day-level bulk button is its own permission. A button only
  // appears at Edit level — View is meaningless for pure actions. No
  // stored row = hide, so every profile starts without them until
  // explicitly granted. (Replaced the combined 'bulk-actions' key on
  // 8 Sep 2026; any stored rows for that old key are ignored.)
  { pageKey: 'game-planner', sectionKey: 'ai-allocate', label: 'AI Allocate', pageLabel: 'Game Planner' },
  { pageKey: 'game-planner', sectionKey: 'import-roster', label: 'Import Roster', pageLabel: 'Game Planner' },
  { pageKey: 'game-planner', sectionKey: 'all-attended', label: 'All Attended', pageLabel: 'Game Planner' },
  { pageKey: 'game-planner', sectionKey: 'clear-roster', label: 'Clear Roster', pageLabel: 'Game Planner' },
  { pageKey: 'attendance', sectionKey: 'attendance', label: 'Attendance', pageLabel: 'Attendance' },
  { pageKey: 'court-planner', sectionKey: 'court-planner', label: 'Court Planner', pageLabel: 'Court Planner' },
  { pageKey: 'rosters', sectionKey: 'rosters', label: 'Home', pageLabel: 'Home' },
  // Note: ('rosters','email-rosters') was removed when the Email Roster
  // workflow moved to the Message page on 20 May 2026. That action is
  // now gated by ('message','send-roster').
  { pageKey: 'admin', sectionKey: 'person', label: 'People Management', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'availability', label: 'Availability', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'referee-qualifications', label: 'Referee Qualifications', pageLabel: 'Qualifications' },
  { pageKey: 'admin', sectionKey: 'bench-qualifications', label: 'Bench Official Qualifications', pageLabel: 'Qualifications' },
  { pageKey: 'admin', sectionKey: 'court-qualifications', label: 'Court Official Qualifications', pageLabel: 'Qualifications' },
  { pageKey: 'admin', sectionKey: 'division-age-groups', label: 'Division Age Groups', pageLabel: 'Qualifications' },
  { pageKey: 'reports', sectionKey: 'reports', label: 'Reports', pageLabel: 'Reports' },
  { pageKey: 'payroll', sectionKey: 'payroll-maintenance', label: 'Payroll Maintenance', pageLabel: 'Payroll' },
  { pageKey: 'admin', sectionKey: 'business-banking', label: 'Business Banking', pageLabel: 'Admin' },
  { pageKey: 'payroll', sectionKey: 'process-payroll', label: 'Process Payroll', pageLabel: 'Payroll' },
  { pageKey: 'payroll', sectionKey: 'payroll-report', label: 'Payroll Report', pageLabel: 'Payroll' },
  { pageKey: 'admin', sectionKey: 'games', label: 'Games', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'settings', label: 'Settings', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'budget', label: 'Budget', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'audit', label: 'Audit', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'error-log', label: 'Error Log', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'security-profiles', label: 'Security Profiles', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'site-permissions', label: 'Site Permissions', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'ai-management', label: 'AI Management', pageLabel: 'Admin' },
  { pageKey: 'coach-planner', sectionKey: 'coach-planner', label: 'Coach Planner', pageLabel: 'Coach Planner' },
  // Swish IQ (trial feedback). Any View/Edit row shows the Swish IQ tab and
  // lets the person see every session; Edit enables changes in that area.
  // Coaches assigned to a session need none of these to use coach mode.
  { pageKey: 'swishiq', sectionKey: 'sessions', label: 'Sessions (setup, trial days, coaches, outcomes)', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'audit-log', label: 'Audit log (off unless granted)', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'players', label: 'Trial players (trial fields, CSV import, custom fields)', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'skills', label: 'Skills and descriptors', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'reports', label: 'Trial reports (player fit)', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'ai-feedback', label: 'AI feedback', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'player-emails', label: 'Player emails (eliminated players, outcome emails)', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'swishiq', sectionKey: 'coach-mode', label: 'Coach mode for any session (unlock iPads)', pageLabel: 'SwishIQ Trials' },
  { pageKey: 'admin', sectionKey: 'program-types', label: 'Program Types', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'competition-mapping', label: 'BC Registrations', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'referee-codes', label: 'Referee Codes', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'coach-codes', label: 'Coach Codes', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'coach-qualifications', label: 'Coach Qualifications', pageLabel: 'Qualifications' },
  { pageKey: 'admin', sectionKey: 'participants', label: 'Participants', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'active-referees', label: 'Active Referees', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'active-bench', label: 'Active Bench Officials', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'active-coaches', label: 'Active Coaches', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'active-court', label: 'Active Court Officials', pageLabel: 'People' },
  { pageKey: 'admin', sectionKey: 'roster-response', label: 'Roster Response', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'sms', label: 'SMS Configuration & Test Send', pageLabel: 'Admin' },
  { pageKey: 'admin', sectionKey: 'reporting', label: 'Reporting (Court Supervisors auto-email)', pageLabel: 'Admin' },
  { pageKey: 'judiciary', sectionKey: 'judiciary', label: 'Judiciary (tech foul summary, suspensions)', pageLabel: 'Judiciary' },
  { pageKey: 'message', sectionKey: 'send-roster', label: 'SMS One-Person Roster', pageLabel: 'Message' },
  { pageKey: 'message', sectionKey: 'custom', label: 'Custom SMS to Selected', pageLabel: 'Message' },
  { pageKey: 'message', sectionKey: 'bulk-role', label: 'Bulk SMS by Role', pageLabel: 'Message' },
  { pageKey: 'message', sectionKey: 'bulk-participants', label: 'Bulk SMS to Participants', pageLabel: 'Message' },
];

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState({});
  const [profileName, setProfileName] = useState('');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      // JWT token is automatically attached by the global fetch interceptor
      // The backend extracts the user email from the verified JWT token
      const user = getAuditUser();
      const headers = {};
      // Include audit headers if available (backward compatibility)
      if (user.email) headers['X-Audit-User-Email'] = user.email;
      if (user.name) headers['X-Audit-User-Name'] = user.name;

      const response = await fetch(`${API_BASE}/my-permissions`, { headers });

      if (response.status === 401) {
        // JWT token might not be ready yet - retry once after a short delay
        console.warn('Auth token not ready, retrying in 1s...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        const retryResponse = await fetch(`${API_BASE}/my-permissions`, { headers });
        if (retryResponse.ok) {
          const data = await retryResponse.json();
          setPermissions(data.permissions || {});
          setProfileName(data.profileName || '');
          setAccessDenied(false);
          setLoading(false);
          return;
        }
        if (retryResponse.status === 403) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (response.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || {});
        setProfileName(data.profileName || '');
        setAccessDenied(false);
      } else {
        setAccessDenied(true);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  const getPermission = (pageKey, sectionKey) => {
    return permissions[pageKey]?.[sectionKey] || 'hide';
  };

  const canAccessPage = (pageKey) => {
    const pagePerms = permissions[pageKey];
    if (!pagePerms) return false;
    return Object.values(pagePerms).some(level => level === 'edit' || level === 'view');
  };

  const canView = (pageKey, sectionKey) => {
    const level = getPermission(pageKey, sectionKey);
    return level === 'edit' || level === 'view';
  };

  const canEdit = (pageKey, sectionKey) => {
    return getPermission(pageKey, sectionKey) === 'edit';
  };

  return (
    <PermissionsContext.Provider value={{
      permissions,
      profileName,
      loading,
      accessDenied,
      getPermission,
      canAccessPage,
      canView,
      canEdit,
      refreshPermissions: loadPermissions
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
